import { type ReactElement } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import {
    StatusBadge,
    type StatusConfig,
} from '@apps/web/components/ui/status_badge';
import { type StatusIntent } from '@apps/web/components/ui/status_tokens';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { getDataSourceDisplayName } from '@apps/web/lib/data_source_utils';
import {
    getOutreachBadgeDisplayStatus,
    getOutreachStatusLabel,
    showsFoundAvailableCaregiversStatus,
} from '@apps/web/lib/outreach_status';
import {
    getEffectiveScheduledOutreachStartAt,
    getScheduledOutreachState,
} from '@apps/web/lib/outreach_utils';
import { useOrganization } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type components,
    type DisplayOutreachStatus,
} from '@libraries/typescript/api_client';

type AutoStartManualReviewReason =
    components['schemas']['AutoStartManualReviewReason'];

const OUTREACH_STATUS_INTENT: Record<DisplayOutreachStatus, StatusIntent> = {
    pending: 'neutral',
    paused: 'neutral',
    in_progress: 'info',
    awaiting_confirmation: 'positive',
    filled: 'positive',
    unfilled: 'neutral',
    cancelled: 'neutral',
    shift_missing: 'warning',
};

type OutreachStatusBadgeProps = {
    readonly status: DisplayOutreachStatus;
    readonly acceptedCaregiverCount?: number;
    // Caregivers with a yes on a still-open shift; see
    // getOutreachBadgeDisplayStatus. When omitted the badge falls back to
    // acceptedCaregiverCount.
    readonly openAcceptedCaregiverCount?: number | null;
    readonly hasAssignedCaregiver?: boolean;
    readonly autoStartManualReviewReason?: AutoStartManualReviewReason | null;
    readonly outreachMetadata?: Record<string, unknown> | null;
    readonly organizationId?: string | null;
    readonly className?: string;
};

function getMissingShiftTooltipText(ehrDisplayName: string): string {
    return `This shift no longer appears in ${ehrDisplayName} — it was most likely deleted, rescheduled, or edited there (an edit changes the shift's ID, so Phoebe can no longer match it). Phoebe no longer treats it as an active shift; open it in ${ehrDisplayName} to confirm, or refresh the shift to re-check.`;
}

function MissingShiftTooltip({
    badge,
    ehrDisplayName,
}: {
    readonly badge: ReactElement;
    readonly ehrDisplayName: string;
}) {
    return (
        <Tooltip>
            <TooltipTrigger asChild={true}>
                <span tabIndex={0}>{badge}</span>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
                <p>{getMissingShiftTooltipText(ehrDisplayName)}</p>
            </TooltipContent>
        </Tooltip>
    );
}

function MissingShiftBadge({
    badge,
    organizationId,
}: {
    readonly badge: ReactElement;
    readonly organizationId: string | null;
}) {
    const { data: organization } = useOrganization(organizationId, {
        reason: 'outreach-status-badge',
    });
    const ehrDisplayName = organization?.general_settings?.primary_data_source
        ? getDataSourceDisplayName(
              organization.general_settings.primary_data_source,
          )
        : 'the source EHR';

    return (
        <MissingShiftTooltip badge={badge} ehrDisplayName={ehrDisplayName} />
    );
}

export function OutreachStatusBadge({
    status,
    acceptedCaregiverCount,
    openAcceptedCaregiverCount,
    hasAssignedCaregiver,
    autoStartManualReviewReason,
    outreachMetadata,
    organizationId: explicitOrganizationId,
    className,
}: OutreachStatusBadgeProps) {
    const contextOrganizationId = useOrganizationId();
    const organizationId =
        explicitOrganizationId !== undefined
            ? explicitOrganizationId
            : contextOrganizationId;
    const displayStatus = getOutreachBadgeDisplayStatus({
        status,
        acceptedCaregiverCount,
        openAcceptedCaregiverCount,
    });
    const useFoundAvailableCaregiversPalette =
        showsFoundAvailableCaregiversStatus({
            status: displayStatus,
            acceptedCaregiverCount,
        });
    // True for any outreach that carries a scheduled-start stamp: a live or
    // paused schedule, or the durable was_scheduled_start_at that survives
    // firing and cancel. Drives the "Scheduled" chip below.
    const wasScheduled =
        getEffectiveScheduledOutreachStartAt(outreachMetadata) !== null;
    // A pending outreach with a stamp reads "Scheduled"/"Paused" rather than
    // plain "Drafted".
    const scheduledState =
        displayStatus === 'pending' && wasScheduled
            ? getScheduledOutreachState(outreachMetadata)
            : undefined;
    const needsManualReview =
        displayStatus === 'pending' &&
        autoStartManualReviewReason === 'ehr_shift_note_over_limit';
    const config: StatusConfig = needsManualReview
        ? { intent: 'warning', label: 'Needs review' }
        : {
              intent: useFoundAvailableCaregiversPalette
                  ? 'positive'
                  : OUTREACH_STATUS_INTENT[displayStatus],
              label: getOutreachStatusLabel({
                  status: displayStatus,
                  acceptedCaregiverCount,
                  openAcceptedCaregiverCount,
                  hasAssignedCaregiver,
                  scheduledState,
              }),
          };

    const badge = <StatusBadge status={config} className={className} />;
    let badgeWithTooltip = badge;
    if (displayStatus === 'shift_missing') {
        badgeWithTooltip = (
            <MissingShiftBadge badge={badge} organizationId={organizationId} />
        );
    }
    if (needsManualReview) {
        badgeWithTooltip = (
            <Tooltip>
                <TooltipTrigger asChild={true}>
                    <span tabIndex={0}>{badge}</span>
                </TooltipTrigger>
                <TooltipContent className="max-w-sm">
                    <p>
                        An EHR shift note is longer than 10,000 characters.
                        Review the full note before starting this outreach.
                    </p>
                </TooltipContent>
            </Tooltip>
        );
    }
    // A non-pending outreach that was scheduled gets a small "Scheduled" chip
    // beside its outcome so it reads apart from a regular outreach in mixed
    // lists. Pending scheduled ones already read "Scheduled" in their own label.
    if (wasScheduled && displayStatus !== 'pending') {
        return (
            <span className="inline-flex items-center gap-1">
                {badgeWithTooltip}
                <Badge variant="secondary" className="font-normal">
                    Scheduled
                </Badge>
            </span>
        );
    }
    return badgeWithTooltip;
}
